<template>
  <Navbar :title="title" :class="{'show' : showNavbar}">
    <template v-slot:close>
      <div @click="$emit('close')" class="close">
        Cerrar
      </div>
    </template>
    <template v-slot:content>
      <!--Local-->
      <div class="form-group">
        <label for="local">Propiedad</label>
        <select v-model="document.local" class="select" name="local" id="local">
          <option v-for="(e, index) in estates" :value="e" :key="index">{{ e.description }}</option>
        </select>
        <span v-if="this.validationErrors.local" class="error">{{ this.validationErrors.local }}</span>
      </div>
      <!--Type-->
      <div class="form-group">
        <label for="type">Tipo de gasto</label>
        <select v-model="document.type" class="select" name="type" id="type">
          <option v-for="(o, index) in types" :key="index" :value="o">{{ o }}</option>
        </select>
        <span v-if="this.validationErrors.type" class="error">{{ this.validationErrors.type }}</span>
      </div>
      <!--Observation-->
      <div class="form-group">
        <label for="observation">Observación</label>
        <input v-model="document.observation" class="input" type="text" id="observation" name="observation">
        <span v-if="this.validationErrors.observation" class="error">{{ this.validationErrors.name }}</span>
      </div>
      <!--Currency-->
      <div class="form-group">
        <label for="currency">Moneda</label>
        <select v-model="document.currency" class="select" name="currency" id="currency">
          <option v-for="(c, index) in currencies" :value="c" :key="index">{{ c.name }}</option>
        </select>
        <span v-if="this.validationErrors.currency" class="error">{{ this.validationErrors.currency }}</span>
      </div>
      <!--Total-->
      <div class="form-group">
        <label for="rent">Total</label>
        <input v-model.number="document.total" class="input" type="number" id="rent"
               name="rent">
        <span v-if="this.validationErrors.total" class="error">{{ this.validationErrors.total }}</span>
      </div>
      <!--Deadline-->
      <div class="form-group">
        <label for="deadline">Fecha de vencimiento</label>
        <v-date-picker
          id="deadline"
          name="deadline"
          v-model="document.deadline"
          color="red"
          is-expanded
          :timezone="`America/Lima`"
        />
        <span v-if="this.validationErrors.deadline" class="error">{{ this.validationErrors.deadline }}</span>
      </div>
    </template>
    <template v-slot:footer>
      <button :disabled="progress" :class="{'is-loading': progress}" @click="save" class="btn">
        <span v-if="!progress">Guardar</span>
      </button>
    </template>
  </Navbar>
  <Shadow :class="{'show' : showNavbar}"/>
</template>

<script>
import Navbar from '@/components/Navbar'
import Shadow from '@/components/Shadow'
import { auth, db, FieldValue } from '@/firebase/firebaseConfig'
import { EXPENSE_TYPES } from '@/utils/expenseTypes'
import * as yup from 'yup'
import axios from 'axios'

yup.setLocale({
  mixed: {
    required: 'Este campo es obligatorio'
  },
  number: {
    positive: 'Debe ser positivo',
    integer: 'Debe ser entero',
    truncate: 'Formato incorrecto'
  }
})

export default {
  name: 'NewExpense',
  props: {
    showNavbar: {
      type: Boolean,
      default: () => {
        return false
      }
    },
    title: {
      type: String
    },
    estates: {
      type: Array,
      required: true
    },
    // Cuando el módulo está embebido en el detalle de un local, preseleccionar ese local
    defaultEstate: {
      type: Object,
      default: null
    }
  },
  components: {
    Navbar,
    Shadow
  },
  data () {
    return {
      document: {},
      schema: yup.object().shape({
        deadline: yup.date().required().label('Vencimiento'),
        total: yup.number().required().positive().label('Total'),
        type: yup.string().required().label('Tipo'),
        currency: yup.object().required().label('Moneda'),
        local: yup.object().required().label('Local')
      }),
      validationErrors: {},
      progress: false,
      // TC (venta SUNAT) vigente al momento de crear el gasto; se dispara async al abrir
      // el form (lazy) y se persiste como `exchangeRate` para que la conversión a USD sea
      // histórica y no dependa del TC de hoy. Fallback 3.7 si la request falla/no llegó.
      exchangeRate: 3.7,
      types: EXPENSE_TYPES,
      currencies: [
        {
          name: 'Soles',
          code: 'PEN'
        },
        {
          name: 'Dólares',
          code: 'USD'
        }
      ]
    }
  },
  watch: {
    showNavbar (value) {
      if (value) {
        this.document = {}
        this.validationErrors = {}
        this.exchangeRate = 3.7
        // Disparar el TC async al abrir (lazy: no bloquea la UI). Si no llega a tiempo o
        // falla, al guardar usamos el fallback (3.7).
        this.getExchangeRate()
        // Preseleccionar el local cuando el detalle de propiedad fuerza el estate
        if (this.defaultEstate) {
          const match = this.estates.find(e => e.id === this.defaultEstate.id)
          if (match) this.document.local = match
        }
      }
    }
  },
  methods: {
    async getExchangeRate () {
      try {
        const now = new Date()
        const month = String(now.getMonth() + 1).padStart(2, '0')
        const date = String(now.getDate()).padStart(2, '0')
        const res = await axios.post(
          'https://southamerica-east1-inqi-5fe1e.cloudfunctions.net/vendors-getExchangeRate',
          { year: now.getFullYear(), month, date },
          { headers: { Authorization: 'Bearer ' + (process.env.VUE_APP_EXCHANGE_RATE_TOKEN || '78b1deb4-1b27-4943-9d83-818285cc59d6') } }
        )
        this.exchangeRate = res.data.venta || 3.7
      } catch { this.exchangeRate = 3.7 }
    },
    async save () {
      try {
        this.progress = true
        this.validationErrors = {}
        await this.schema.validate({ ...this.document }, { abortEarly: false })
        const obj = {
          ...this.document,
          // TC histórico: se guarda SIEMPRE (aunque el gasto sea en USD, como registro).
          // La conversión PEN→USD al leer usa este valor, no el TC de hoy.
          exchangeRate: this.exchangeRate,
          createdAt: FieldValue.serverTimestamp(),
          uid: auth.currentUser.uid,
          state: true,
          paid: false
        }
        const doc = await db.collection('expenses').add({
          ...obj
        })
        obj.id = doc.id
        this.$emit('add', obj)
        this.$emit('close')
      } catch (err) {
        if (err.inner) {
          err.inner.forEach(error => {
            if (error.path) {
              this.validationErrors[error.path] = error.message
            }
          })
        } else {
          this.$toast.error('Error al guardar gasto')
        }
        console.log(err)
      } finally {
        this.progress = false
      }
    }
  }
}
</script>

<style scoped>

</style>
